import {useLayoutEffect, type RefObject} from "react";

/**
 * Publishes a scroll container's vertical scrollbar gutter, in CSS pixels, as the custom property
 * `property` on that container, so its stylesheet can give the gutter back to the content.
 *
 * The gutter is the box width the scrollbar takes: `offsetWidth - clientWidth` for a container with no
 * side borders. It is 0 under overlay scrollbars (macOS, phones) and about 10px under classic ones
 * (Windows, Linux) with `scrollbar-width: thin`, and no CSS value can name it, which is why it is
 * measured. Re-read on window resize, because browser zoom changes the gutter's CSS width.
 */
export function useScrollGutter(ref: RefObject<HTMLElement | null>, property: string): void {
	useLayoutEffect(() => {
		const node = ref.current;
		if (node === null) return;
		const publish = () => node.style.setProperty(property, `${Math.max(node.offsetWidth - node.clientWidth, 0)}px`);
		publish();
		window.addEventListener("resize", publish);
		return () => window.removeEventListener("resize", publish);
	}, [ref, property]);
}
